import { encodeModule } from 'helpers/helpers';
import { notifyError } from 'services/request';
import { ITabsActions, ITabsActionTypes } from 'store/actions/tabs';
import { IRefIds } from 'constants/refs';
import { IModule } from 'types/module';

const initialTabs = {
  activeTabId: null as string | null,
  tabs: [] as IModule[],
  moduleNames: {} as { [key: string]: string },
  pendingTabIds: {} as Record<string, boolean>,
};

export type ITabs = typeof initialTabs;

export function tabsReducer(state = initialTabs, action: ITabsActions): ITabs {
  switch (action.type) {
    case ITabsActionTypes.NAMES_LOADED:
      return {
        ...state,
        moduleNames: action.payload,
      };

    case ITabsActionTypes.OPENED:
      const { payload: rawTab } = action;
      const refIdMap: Partial<Record<IRefIds, IRefIds>> = {
        [IRefIds.lessons]: IRefIds.lessonOutline,
        [IRefIds.translation]: IRefIds.sourceAdaptation,
      };
      const tab = rawTab.refId in refIdMap ? { ...rawTab, refId: refIdMap[rawTab.refId]! } : rawTab;
      const id = state.moduleNames[encodeModule(tab)] as string | undefined;

      if (!id) { // No permission
        notifyError('You do not have permission to open this module.');
        return state;
      }

      const existingTab = state.tabs.find((currentTab) => currentTab.id === id);
      const pendingTabIds = { ...state.pendingTabIds };

      let nextTabs: IModule[];
      let nextActiveTabId: string;

      if (existingTab) {
        nextTabs = state.tabs.map((currentTab) => {
          if (currentTab.id === id) {
            return { ...currentTab, ...tab };
          }
          return currentTab;
        });
        nextActiveTabId = existingTab.id;
      } else {
        nextTabs = [...state.tabs, { ...tab, id }];
        nextActiveTabId = id;
      }

      pendingTabIds[id] = true;

      return {
        ...state,
        tabs: nextTabs,
        activeTabId: nextActiveTabId,
        pendingTabIds,
      };

    case ITabsActionTypes.CLOSED:
      const { payload: closedTabId } = action;
      const closedTabIndex = state.tabs.findIndex((tab) => tab.id === closedTabId);
      const activeTabIndex = state.tabs.findIndex((tab) => tab.id === state.activeTabId);

      const newTabs = [] as IModule[];

      state.tabs.forEach((tab) => {
        const notClosed = tab.id !== closedTabId;
        if (notClosed) {
          newTabs.push(tab);
        }
      });

      let newActiveTabId = null;

      const pendingTabIdsAfterClose = { ...state.pendingTabIds };
      if (pendingTabIdsAfterClose[closedTabId]) {
        delete pendingTabIdsAfterClose[closedTabId];
      }

      if (newTabs.length) {
        if (activeTabIndex === closedTabIndex) {
          const validActiveTabIndex = Math.min(newTabs.length - 1, activeTabIndex);
          newActiveTabId = newTabs[validActiveTabIndex].id;
        } else if (activeTabIndex > closedTabIndex) {
          newActiveTabId = newTabs[activeTabIndex - 1].id;
        } else {
          newActiveTabId = state.activeTabId;
        }
      }

      if (newActiveTabId && pendingTabIdsAfterClose[newActiveTabId]) {
        delete pendingTabIdsAfterClose[newActiveTabId];
      }

      return {
        ...state,
        tabs: newTabs,
        activeTabId: newActiveTabId,
        pendingTabIds: pendingTabIdsAfterClose,
      };

    case ITabsActionTypes.REORDERED: {
      const { sourceIndex, destinationIndex } = action.payload;
      const reorderedTabs = [...state.tabs];
      const [removed] = reorderedTabs.splice(sourceIndex, 1);
      reorderedTabs.splice(destinationIndex, 0, removed);
      return { ...state, tabs: reorderedTabs };
    }

    case ITabsActionTypes.CHANGED_ACTIVE:
      const { payload: activeTabId } = action;

      if (!activeTabId) {
        return {
          ...state,
          activeTabId,
        };
      }

      if (!state.pendingTabIds[activeTabId]) {
        return {
          ...state,
          activeTabId,
        };
      }

      const nextPendingTabIds = { ...state.pendingTabIds };
      delete nextPendingTabIds[activeTabId];

      return {
        ...state,
        activeTabId,
        pendingTabIds: nextPendingTabIds,
      };

    default:
      return state;
  }
}
